import { useState, useEffect } from 'react';

type Breakpoint = 'mobile' | 'tablet' | 'desktop';

interface BreakpointInfo {
  breakpoint: Breakpoint;
  columns: number;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

function infoFor(isMobile: boolean, isTablet: boolean): BreakpointInfo {
  if (isMobile) return { breakpoint: 'mobile', columns: 1, isMobile: true, isTablet: false, isDesktop: false };
  if (isTablet) return { breakpoint: 'tablet', columns: 2, isMobile: false, isTablet: true, isDesktop: false };
  return { breakpoint: 'desktop', columns: 4, isMobile: false, isTablet: false, isDesktop: true };
}

// ============================================================================
// useBreakpoint — ДЕТЕРМИНИРОВАННЫЙ брейкпоинт через matchMedia, а НЕ
// window.innerWidth. Причина (2026-08-30, консультант): виртуальная
// клавиатура Android меняет window.innerWidth → ложные перерисовки
// (isMobile мигает при открытии клавиатуры). matchMedia смотрит на
// реальный вьюпорт (media-запрос), не зависит от клавиатуры.
// ============================================================================
export function useBreakpoint(): BreakpointInfo {
  const mqMobile = '(max-width: 767px)';
  const mqTablet = '(min-width: 768px) and (max-width: 1023px)';

  const [info, setInfo] = useState<BreakpointInfo>(() => {
    if (typeof window === 'undefined') return infoFor(false, false);
    return infoFor(
      window.matchMedia(mqMobile).matches,
      window.matchMedia(mqTablet).matches,
    );
  });

  useEffect(() => {
    const m1 = window.matchMedia(mqMobile);
    const m2 = window.matchMedia(mqTablet);
    const update = () => setInfo(infoFor(m1.matches, m2.matches));
    // Современный addEventListener (с passive) — иначе TS/браузер ругается
    m1.addEventListener('change', update, { passive: true });
    m2.addEventListener('change', update, { passive: true });
    return () => {
      m1.removeEventListener('change', update);
      m2.removeEventListener('change', update);
    };
  }, []);

  return info;
}
